(ns app.front.view.dispatch.subs
  (:require [app.domain.blackout.model :as blackout-model]
            [app.domain.booking.model :as booking-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.party.model :as party-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.process :as process]
            [app.front.util :as futil]
            [app.front.validate :as validate]
            [app.front.view.inspections.booking-utils :as butil]
            [app.front.view.workspace.get :as ws-get]
            [clojure.data :as cd]
            [clojure.edn :as edn]
            [clojure.set :as set]
            [core.db.entity.util :as eu]
            [core.front.date-util :as date-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s :refer [defschema]]))

(def interceptors [(rf/inject-cofx :tr)])

;; ID of selected booking:
(rf/reg-sub ::selected-booking-id
  :-> ::selected-booking-id)

(defn get-date [db & [_]]
  (or (get db ::date) (date-util/today-start-of-day)))

(rf/reg-sub ::date
  get-date)

(rf/reg-sub ::drag-over-booking :-> ::drag-over-booking)

(rf/reg-sub ::selected-authority-group-id :-> ::selected-authority-group-id)

(rf/reg-sub ::use-unified-view? :-> ::use-unified-view?)

(rf/reg-sub ::mode
  (fn [db _] (::mode db :requests)))

(rf/reg-sub ::select-authority-group-items
  :<- [::dispatcher-authority-groups]
  :<- [::time-slots-identical?]
  :<- [:tr]
  (fn [[groups identical-slots? tr] _]
    (->> groups
         (map (fn [[id {:keys [:group/name]}]]
                {:label name
                 :value id}))
         (sort-by :label)
         (concat (when (and identical-slots? (next groups))
                   [{:label (tr [:dispatching/unified-view])
                     :value :all}])))))


(rf/reg-sub ::selected-authority-group-item
  :<- [::select-authority-group-items]
  :<- [::selected-authority-group-id]
  (fn [[items id] _] (first (filter #(= id (:value %)) items))))


(rf/reg-sub ::selected-group-gis-customer-id
  :<- [::dispatcher-authority-groups]
  :<- [::selected-authority-group-id]
  (fn [[groups id] _]
    (get-in groups [id :domain-group-id])))


(rf/reg-sub ::visible-inspector-ids-db
  :-> ::visible-inspector-ids)


(rf/reg-sub ::notes-dialog-open?
  #(get % ::notes-dialog-open? false))


(rf/reg-sub ::dispatch-notes-draft
  #(get % ::dispatch-notes-draft ""))


(def core-inspector-sort-options
  [:dispatching.inspector-sort/availability
   :dispatching.inspector-sort/qualifications])

(def all-inspector-sort-options
  (set (conj core-inspector-sort-options
             :dispatching.inspector-sort/location)))

(def inspector-local-storage-key "enabled-inspector-sort-options")
(def group-select-local-storage-key "selected-dispatching-group")

(defn- enabled-inspector-sort-options [db]
  (or (::enabled-inspector-sort-options db)
      (-> (futil/local-storage-get inspector-local-storage-key)
          edn/read-string)
      all-inspector-sort-options))

(defn area-mismatch? [areas other-area-ids]
  (->> areas
       (map :id)
       set
       (set/intersection other-area-ids)
       empty?))

(defn sort-score [sort-enabled? areas booking booking-counts event-counts inspector]
  (let [workspace                 (-> booking
                                      ;; inspection
                                      :entity/parent
                                      ;; execution
                                      :entity/parent
                                      ;; workspace
                                      :entity/parent)
        inspection-group-id       (-> workspace :domain/authority-group :db/id)
        qualifications            (-> inspector
                                      (inspection-model/get-inspector-qualifications #{inspection-group-id})
                                      vals
                                      first
                                      set)
        booking-areas             (when areas
                                    (-> workspace
                                        :workspace/primary-property
                                        :entity/location
                                        :db/id
                                        areas
                                        set))
        {:keys [primary-areas
                secondary-areas]} inspector
        get-qualification-id      #(get % :db/id %)
        [extras _ applicable]     (cd/diff (->> qualifications
                                                (map get-qualification-id)
                                                set)
                                           (->> booking
                                                :entity/parent
                                                :inspection/inspector-qualifications
                                                (map get-qualification-id)
                                                set))
        primary-mismatch?         (area-mismatch? primary-areas booking-areas)
        secondary-mismatch?       (area-mismatch? secondary-areas booking-areas)]
    (cond-> []
      (sort-enabled? :dispatching.inspector-sort/location)
      ;; any areas match (note that false is sorted first)
      (conj (and primary-mismatch? secondary-mismatch?))

      (sort-enabled? :dispatching.inspector-sort/qualifications)
      ;; prefer those that have the greatest number of matching qualifications of matches
      (conj (-> applicable count (* -1)))

      (sort-enabled? :dispatching.inspector-sort/location)
      ;; then prefer those whose primary area matches
      (conj primary-mismatch?)

      (sort-enabled? :dispatching.inspector-sort/availability)
      ;; then those first that have less things on that day
      (conj (+ (get booking-counts (:db/id inspector)) (get event-counts (:db/id inspector))))

      (sort-enabled? :dispatching.inspector-sort/availability)
      ;; between things to do, bookings are prioritized
      (conj (get booking-counts (:db/id inspector)))

      (sort-enabled? :dispatching.inspector-sort/qualifications)
      ;; among those, sort those that have unnecessary qualifications lower
      (conj (count extras))

      ;; and finally sort by first & last name
      :always
      (concat [(:user/first-name inspector)
               (:user/last-name inspector)])
      :always vec)))


(rf/reg-sub ::visible-inspectors
  :<- [::entities/context]
  :<- [::visible-inspector-ids]
  :<- [::sort-order]
  :<- [::selected-authority-group-id]
  :<- [::use-unified-view?]
  (fn [[ctx ids sort-order selected-group-id use-unified?] _]
    (cond->> (keep #(entities/find-entity ctx %) ids)
      (and (not use-unified?) selected-group-id)
      (filter #(-> % :authority-group-ids set (contains? selected-group-id)))

      :always
      (sort-by (if sort-order
                 (comp sort-order :db/id)
                 (juxt :user/first-name :user/last-name))))))


(rf/reg-sub ::external-events-by-user
  :<- [::entities/entities {:entity/type :entity.type/external-event}]
  (fn [external-events _]
    (->> external-events
         (group-by #(-> % :external-event/user :db/id)))))


(rf/reg-sub ::external-events-for-user
  :<- [::external-events-by-user]
  (fn [external-events-by-user [_ user-id]]
    (get external-events-by-user user-id)))


(defn all-day-event? [event]
  (let [full-24-hrs-in-ms (- (* 24 60 60 1000) 1)]
    (and event
         (>= (- (-> event :external-event/end .getTime)
                (-> event :external-event/start .getTime))
             full-24-hrs-in-ms))))


(rf/reg-sub ::max-all-day-event-count
  :<- [::external-events-by-user]
  (fn [events-by-user [_ date]]
    (->> events-by-user
         (map (fn [[_ events]]
                (->> events
                     (filter #(and
                                (all-day-event? %)
                                (date-util/overlap? (:external-event/start %)
                                                    (:external-event/end %)
                                                    date
                                                    (date-util/end-of-day date))))
                     count)))
         (apply max 0))))


(rf/reg-sub ::dispatcher-authority-groups :-> ::dispatcher-authority-groups)


(defn ag-ids+dg-ids+scopes+prop-i18n-mapping-for-create [[auth-groups selected-group-id unified-view?]]
  (->> auth-groups
       (mapcat (fn [[group-id {:keys [scopes domain-group-id :group/property-i18n-mapping]}]]
                 (when (or unified-view? (= selected-group-id group-id))
                   (->> scopes
                        (map (fn [scope]
                               [group-id domain-group-id scope property-i18n-mapping]))))))
       distinct))


(rf/reg-sub ::ag-ids+dg-ids+scopes+prop-i18n-mapping-for-create
  :<- [::dispatcher-authority-groups]
  :<- [::selected-authority-group-id]
  :<- [::use-unified-view?]
  ag-ids+dg-ids+scopes+prop-i18n-mapping-for-create)


(rf/reg-sub ::time-slot-data-for-group
  :<- [::selected-authority-group-id]
  :<- [::dispatcher-authority-groups]
  (fn [[group-id time-slot-data] _]
    (get time-slot-data group-id)))


(rf/reg-sub ::group-tz
  :<- [::time-slot-data-for-group]
  :-> :group/tz)


(rf/reg-sub ::time-slots-for-selected-date
  :<- [::group-availabilities]
  :<- [::group-blackouts]
  :<- [::date]
  :<- [::group-tz]
  (fn [[group-availabilities group-blackouts date tz] _]
    ;; Note that .getDay must be taken from the non-converted date, because it returns the browser-local weekday number.
    ;; Otherwise, users located in a later timezone than the group timezone would get the wrong weekday.
    (let [weekday (some-> date .getDay)
          date    (date-util/in-tz date tz)]
      (when-not (some (partial blackout-model/applies-to-date?
                               date)
                      group-blackouts)
        (->> (butil/valid-availability date group-availabilities)
             :availability/time-slots
             (butil/time-slots-for-weekday weekday))))))


(rf/reg-sub ::group-availabilities
  :<- [::time-slot-data-for-group]
  :<- [::entities/context]
  (fn [[time-slot-data ctx] _]
    (->> (:availabilities time-slot-data)
         (map (partial entities/find-entity ctx)))))


(rf/reg-sub ::group-time-slots
  :<- [::group-availabilities]
  (fn [availablities _]
    (->> availablities
         (sort-by :entity/valid-from)
         last
         :availability/time-slots)))


(rf/reg-sub ::group-blackouts
  :<- [::time-slot-data-for-group]
  :-> :blackouts)

(defn time-slots-identical? [{:keys [groups date ctx]}]
  (->> groups
       vals
       (map (fn [group]
              (let [time-slots (->> group
                                    :availabilities
                                    (map (partial entities/find-entity ctx))
                                    (butil/valid-availability date)
                                    :availability/time-slots)]
                (when (butil/allow-date? (:group/tz group)
                                         time-slots
                                         (:blackouts group)
                                         date)
                  (->> time-slots
                       (map #(select-keys % [:time-slot/weekday
                                             :time-slot/start-hour
                                             :time-slot/start-minute
                                             :time-slot/end-hour
                                             :time-slot/end-minute]))
                       set
                       (assoc (select-keys group [:group/tz]) :time-slots))))))
       (apply =)))

(rf/reg-sub ::time-slots-identical?
  :<- [::dispatcher-authority-groups]
  :<- [::date]
  :<- [::entities/context]
  (fn [[groups date ctx] _]
    (time-slots-identical? (util/mmap groups date ctx))))


(rf/reg-sub ::time-slots-identical-for-date?
  :<- [::dispatcher-authority-groups]
  :<- [::entities/context]
  (fn [[groups ctx] [_ date]]
    (time-slots-identical? (util/mmap groups date ctx))))


(rf/reg-sub ::active-authority-group-ids
  :<- [::time-slots-identical?]
  :<- [::selected-authority-group-id]
  :<- [::dispatcher-authority-groups]
  :<- [::use-unified-view?]
  (fn [[identical? selected groups use-unified?] _]
    (if (and identical? use-unified?)
      (->> groups keys set)
      #{selected})))


;;;; # Email Recipients


(defschema PartyEmailRecipient
  ;; TODO: Make less sloppy:
  (st/merge {:party-entity-id s/Int}
            (st/optional-keys {:email                s/Str
                               :business             s/Any
                               :roles                [s/Any]
                               :requester?           s/Bool
                               :workspace-authority? s/Bool})))



(def ^:private valid-party-email-recipient! (s/validator PartyEmailRecipient))


(s/defn ^:private party->email-recipient :- (s/maybe PartyEmailRecipient) [party]
  (when (party-model/notifiable? party)
    (if-some [email (party-model/get-email party)]
      {:party-entity-id (:db/id party)
       :roles           (:party/party-roles party)
       :email           email}
      (when-some [business (:party/business party)]
        {:party-entity-id (:db/id party)
         :roles           (:party/party-roles party)
         :business        business}))))


(s/defn ^:private merge-party-recipients :- PartyEmailRecipient
  [recipient :- PartyEmailRecipient, recipient* :- PartyEmailRecipient]
  {:pre [(= (:email recipient) (:email recipient*))]}
  (update recipient :roles set/union (:roles recipient*)))


(s/defn ^:private combine-party-mail-roles :- #{PartyEmailRecipient}
  "Merge recipients with the same email address, unioning their `:roles`."
  [party-recipients]
  (->> party-recipients
       (group-by :email)
       vals
       (map (fn [recipients] (reduce merge-party-recipients recipients)))
       set))


(rf/reg-sub ::party-recipient-candidates
  :<- [::entities/entity-db]
  (fn [entity-db [_ workspace creator]]
    (let [party-recipients  (->> workspace
                                 workspace-model/workspace-execution
                                 party-model/phase-parties
                                 (keep party->email-recipient))
          creator-recipient (when-some [creator-entity (->> creator :db/id (get entity-db))]
                              (when-some [email (:user/email creator-entity)]
                                (valid-party-email-recipient!
                                  {:party-entity-id      (:db/id creator)
                                   :email                email
                                   :requester?           true
                                   :workspace-authority? (-> creator-entity
                                                             :authority-group-ids
                                                             (contains? (-> workspace :domain/authority-group :db/id)))})))
          party-recipients  (cond->> party-recipients
                              creator-recipient (cons creator-recipient))]
      (->> party-recipients
           distinct
           combine-party-mail-roles))))


(rf/reg-sub ::party-mail-recipient-ids
  (fn [db [_ candidates]]
    (get-in db [::confirmation-mail :party-mail-recipients]
            ;; Default values are parties that have specific roles and the requester unless they are an authority.
            (->> candidates
                 (filter #(let [role-key? (->> % :roles (map :party-role/key) set)]
                            (or (some role-key? inspection-model/concerned-roles)
                                (and (:requester? %)
                                     (not (:workspace-authority? %))))))
                 (map :party-entity-id)
                 set))))


(rf/reg-event-db ::set-party-mail-recipient-ids
  (fn [db [_ recipients]]
    (assoc-in db [::confirmation-mail :party-mail-recipients] recipients)))


(rf/reg-sub ::additional-mail-recipients
  #(get-in % [::confirmation-mail :additional-mail-recipients] #{}))


(rf/reg-event-db ::set-additional-mail-recipients
  (fn [db [_ recipients]]
    (assoc-in db [::confirmation-mail :additional-mail-recipients] recipients)))


(rf/reg-sub ::new-additional-mail-recipient
  #(get-in % [::confirmation-mail :new-additional-mail-recipient]))


(rf/reg-event-db ::set-new-additional-mail-recipient
  (fn [db [_ recipient]]
    (assoc-in db [::confirmation-mail :new-additional-mail-recipient] recipient)))

(def new-mail-recipient-model {:email [[validate/Email :validate/wrong-email-address]]})

(rf/reg-sub ::new-additional-mail-recipient-field-invalid?
  :<- [::new-additional-mail-recipient]
  (fn [new-recipient [_ field]]
    (let [validator #(validate/validate-field % new-recipient new-mail-recipient-model)]
      (validate/field-invalid? validator field))))


(rf/reg-sub ::new-additional-mail-recipient-invalid?
  :<- [::new-additional-mail-recipient]
  (fn [new-recipient [_]]
    (-> (validate/validate-form new-recipient new-mail-recipient-model)
        seq
        boolean)))


(rf/reg-sub ::adding-new-additional-mail-recipient?
  #(-> %
       (get-in [::confirmation-mail :new-additional-mail-recipient])
       some?))


;;;; # Additional Mail Notes


(rf/reg-sub ::add-mail-notes
  #(get-in % [::confirmation-mail :add-mail-notes] false))


(rf/reg-event-db ::toggle-add-mail-notes
  (fn [db [_]]
    (update-in db [::confirmation-mail :add-mail-notes] not)))


(rf/reg-sub ::mail-notes
  (fn [db [_ booking]] (get-in db [::confirmation-mail :mail-notes] (:booking/notes booking))))


(rf/reg-event-db ::set-mail-notes
  (fn [db [_ notes]]
    (assoc-in db [::confirmation-mail :mail-notes] notes)))


(rf/reg-event-db ::reset-confirmation-mail
  (fn [db [_]]
    (dissoc db ::confirmation-mail)))


(rf/reg-sub ::confirmation-mail :-> ::confirmation-mail)


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ group-id]]
    ;; group-id is optional
    (let [default-date (date-util/today-start-of-day)]
      {:db         (merge db
                          {::date                  default-date
                           ::map-visible?          (get db ::map-visible? true)
                           ::pre-selected-group-id group-id})
       :dispatch-n [[::fetch-inspectors group-id :group.role/dispatcher]
                    [::fetch-groups-for-dispatching group-id]
                    [::fetch-bookings default-date]
                    [::fetch-working-areas group-id]
                    [::municipality/load-municipalities false]]})))


(rf/reg-event-fx ::fetch-groups-for-dispatching.success
  (fn [{:keys [db]} [_ status {:keys [groups entities]}]]
    (let [previously-selected-group (->> (futil/local-storage-get group-select-local-storage-key)
                                         edn/read-string)
          default-selected          (if (and previously-selected-group
                                             (not= previously-selected-group :all)
                                             (get groups previously-selected-group))
                                      previously-selected-group
                                      (->> groups
                                           (sort-by (comp :group/name val))
                                           ffirst))
          use-unified?              (and (or (not previously-selected-group)
                                             (= previously-selected-group :all))
                                         (> (count groups) 1)
                                         (time-slots-identical? (util/mmap {:date (get-date db)
                                                                            :ctx  (entities/db->context db)}
                                                                           groups)))]
      {:db         (merge db
                          {::dispatcher-authority-groups groups}
                          (when-not (::selected-authority-group-id db)
                            {::selected-authority-group-id default-selected})
                          (when use-unified?
                            {::use-unified-view? true}))
       :dispatch-n (concat [[::loaded :groups]
                            [::entities/merge-entity-db status entities]]
                           (->> (vals groups)
                                (map :domain-group-id)
                                distinct
                                (mapv (fn [id]
                                        [::municipality/load-map-config id]))))})))


(rf/reg-event-fx ::fetch-groups-for-dispatching
  (fn [{:keys [db]} [_ group-id]]
    {:comm/command {:command    :booking/groups-for-dispatching
                    :method     :get
                    :data       (when group-id
                                  {:group-id group-id})
                    :on-success [::fetch-groups-for-dispatching.success]}
     :db           (assoc-in db [::loading :groups] true)}))


(rf/reg-event-fx ::fetch-bookings
  (fn [{:keys [db]} [_ date]]
    (let [selected-group-id (::selected-authority-group-id db)
          group-tz          (get-in db [::dispatcher-authority-groups selected-group-id :group/tz])
          fixed-group-id    (::pre-selected-group-id db)]
      {:comm/command {:command    :booking/find-for-dispatch
                      :method     :get
                      :data       (merge (when group-tz
                                           {:start (date-util/date-at-time-in-tz date 0 0 group-tz)
                                            :end   (date-util/date-at-time-in-tz date 23 59 59 group-tz)})
                                         (when fixed-group-id
                                           {:group-id fixed-group-id}))
                      :on-success [::bookings-fetched]}
       :db           (-> db
                         (assoc-in [::loading :bookings] true)
                         (update-in [::loading :bookings-initial] (fn [v] (if (some? v) v true))))})))

(rf/reg-event-fx ::bookings-fetched
  (fn [{:keys [db]} [_ status {:keys [entities areas]}]]
    {:dispatch-n [[::entities/merge-entity-db status entities]
                  [::loaded :bookings]
                  [::loaded :bookings-initial]]
     :db         (-> db
                     (assoc ::property-areas areas))}))

(rf/reg-sub ::property-areas
  :-> ::property-areas)

(rf/reg-event-fx
  ::select-authority-group
  (fn [{:keys [db]} [_ group-id]]
    {:db                 (-> (assoc db ::selected-authority-group-id group-id)
                             (assoc ::use-unified-view? false))
     :local-storage-set! [group-select-local-storage-key (pr-str group-id)]}))

(rf/reg-event-fx ::set-use-unified-view
  (fn [{:keys [db]} [_ enabled?]]
    {:db                 (assoc db ::use-unified-view? enabled?)
     :local-storage-set! [group-select-local-storage-key (pr-str :all)]}))

(rf/reg-event-db ::select-mode
  (fn [db [_ mode]]
    (-> db
        (assoc ::mode mode)
        (assoc ::request-editor-open? false))))


(rf/reg-event-fx ::select-date
  (fn [{:keys [db]} [_ date identical?]]
    (let [date (date-util/start-of-day date)]
      {:db         (assoc db ::date date)
       :dispatch-n (concat [[::fetch-bookings date]
                            [::update-sort-order]]
                           (when (not identical?)
                             [[::set-use-unified-view false]]))})))


(rf/reg-event-fx ::set-selected-booking
  (fn [{:keys [db]} [_ booking-id toggle?]]
    (let [is-select? (not (and toggle?
                               (= (::selected-booking-id db) booking-id)))]
      (cond-> {:db (-> db
                       (assoc ::selected-booking-id (when is-select?
                                                      booking-id)))}
        ;; update sort order if booking was not dispatched
        (and (-> db ::entities/entity-db (get booking-id) :booking/assignee nil?)
             is-select?)
        (assoc :dispatch [::update-sort-order])))))

(defn freqs-by-user [db date entity-type date-attr user-attr]
  (->> db
       ::entities/entity-db
       vals
       (filter (eu/is-type-fn entity-type))
       (filter (comp (partial date-util/same-day? date) date-attr))
       (map (comp :db/id user-attr))
       frequencies))

(rf/reg-event-db ::update-sort-order
  (fn [db _]
    (let [sort-options (enabled-inspector-sort-options db)
          e-schema     {:schema (::entities/schema db) :entity-db (::entities/entity-db db)}
          inspectors   (->> db ::visible-inspector-ids set (entities/find-entities e-schema))]
      (if (empty? sort-options)
        (dissoc db ::sort-order)
        (let [selected-booking (->> db ::selected-booking-id (entities/find-entity e-schema))
              areas            (-> db ::property-areas)
              date             (get-date db)
              booking-counts   (freqs-by-user db
                                              date
                                              :entity.type/booking
                                              :booking/actual-start-time
                                              :booking/assignee)
              event-counts     (freqs-by-user db
                                              date
                                              :entity.type/external-event
                                              :external-event/start
                                              :external-event/user)]
          (assoc db ::sort-order (->> inspectors
                                      (map (juxt :db/id
                                                 (partial sort-score
                                                          sort-options
                                                          areas
                                                          selected-booking
                                                          booking-counts
                                                          event-counts)))
                                      (into {}))))))))

(rf/reg-sub ::sort-order
  :-> ::sort-order)

(rf/reg-event-fx ::set-drag-over-booking
  (fn [{:keys [db]} [_ booking]]
    {:db (assoc db ::drag-over-booking booking)}))


(rf/reg-event-db ::set-dragged-booking-auth-group
  (fn [db [_ group-id]]
    (assoc db ::dragged-booking-auth-group group-id)))


(rf/reg-sub ::dragged-booking-auth-group
  #(-> % ::dragged-booking-auth-group))


(rf/reg-event-fx ::set-dispatch-notes-draft
  (fn [{:keys [db]} [_ draft]]
    {:db (assoc db ::dispatch-notes-draft draft)}))


(rf/reg-event-fx ::update-dispatch-notes-draft
  (fn [_ [_ execution-id booking-id draft]]
    {:dispatch-n        [[::set-dispatch-notes-draft draft]
                         [::changes-to-dispatch-notes? true]]
     :dispatch-debounce [{:id      ::save-dispatch-notes-debounce
                          :timeout 500
                          :action  :dispatch
                          :event   [::save-dispatch-notes false execution-id booking-id draft]}]}))


(rf/reg-event-db ::toggle-notes-dialog
  (fn [db [_ open?]]
    (-> db
        (assoc ::notes-dialog-open? open?))))

(rf/reg-event-fx ::open-notes-dialog
  (fn [{:keys [db]} [_ booking]]
    (let [workspace-id (-> booking
                           booking-model/get-workspace
                           :workspace/id)]
      {:db         (-> db
                       (assoc ::dispatch-notes-draft (:booking/dispatch-notes booking)))
       :dispatch-n [[::ws-get/get-workspace workspace-id :entity.type/inspection]
                    [::toggle-notes-dialog true]]})))

(rf/reg-event-fx ::set-visible-inspector-ids
  (fn [{:keys [db]} [_ _status resp]]
    {:db (->> resp
              (filter #(= :entity.type/user (-> % second :entity/type)))
              keys
              (assoc db ::visible-inspector-ids))}))


(rf/reg-event-fx ::fetch-inspectors-success
  (fn [_ [_ status resp]]
    {:dispatch-n [[::entities/merge-entity-db-for-all
                   {:entity/type :entity.type/external-event}
                   status
                   resp]
                  [::set-visible-inspector-ids status resp]]}))


(rf/reg-event-db ::loaded
  (fn [db [_ kind]]
    (assoc-in db [::loading kind] false)))

(rf/reg-sub ::loading
  :-> ::loading)

(rf/reg-sub ::loading?
  :<- [::loading]
  (fn [loading [_ kind]]
    (get loading kind)))

(rf/reg-sub ::inspectors-loading?
  :<- [::loading? :inspectors]
  (fn [val _] val))

(rf/reg-sub ::loading-any?
  :<- [::loading]
  (fn [loading _]
    (->> loading
         vals
         (some true?))))

(rf/reg-event-fx ::fetch-inspectors
  (fn [{:keys [db]} [_ group-id group-role]]
    {:comm/command {:command          :group/inspectors-for-users-groups
                    :method           :get
                    :data             (p/remove-vals nil? (util/mmap group-id group-role))
                    :on-success       [::fetch-inspectors-success]
                    :finally-dispatch [::loaded :inspectors]}
     :db           (assoc-in db [::loading :inspectors] true)}))

(rf/reg-event-fx ::fetch-parties
  (fn [{:keys [db]} [_ workspace-id]]
    {:comm/command {:command    :workspace/get
                    :data       {:workspace/id workspace-id
                                 :find-related [:entity.type/party]}
                    :on-success [::workspace-fetched]}
     :db           (assoc db ::loading-workspace? true)}))

(rf/reg-sub ::loading-workspace?
  (fn [db _]
    (boolean (::loading-workspace? db))))

(rf/reg-event-fx ::workspace-fetched
  (fn [{:keys [db]} [_ status response]]
    {:db       (dissoc db ::loading-workspace?)
     :dispatch [::entities/merge-entity-db status (:entities response)]}))

(rf/reg-event-fx
  ::fetch-working-areas
  (fn [{:keys [db]} [_ group-id]]
    {:comm/command {:command    :group/get-working-areas-as-geojson
                    :method     :get
                    :data       (when group-id
                                  {:group-id group-id})
                    :on-success [::fetch-working-areas.success]}
     :db           (assoc-in db [::loading :working-areas] true)}))

(rf/reg-event-db ::fetch-working-areas.success
  (fn [db [_ _ feature-collection]]
    (-> db
        (assoc ::working-areas-feature-collection feature-collection)
        (assoc-in [::loading :working-areas] false))))


;; Dispatch booking
(rf/reg-event-fx ::dispatch-booking
  (fn [{:keys [db]} [_ inspector-id booking-id execution-id time-slot group-tz all-days-bookings]]
    (let [date              (get db ::date)
          start             (date-util/date-at-time-in-tz date
                                                          (:time-slot/start-hour time-slot)
                                                          (:time-slot/start-minute time-slot)
                                                          group-tz)
          end               (date-util/date-at-time-in-tz date
                                                          (:time-slot/end-hour time-slot)
                                                          (:time-slot/end-minute time-slot)
                                                          group-tz)
          ;; It's possible for the user to drag the booking over a booking assigned to another inspector while
          ;; (re-)dispatching it. In this situation we must not set the :to-id in the request, because the target
          ;; booking would not belong to this target inspector.
          drag-over-booking (when (-> db ::drag-over-booking :booking/assignee :db/id (= inspector-id))
                              (::drag-over-booking db))
          payload           (p/assoc-if {:to-user-id inspector-id
                                         :booking-id booking-id}
                                        :start-time start
                                        :end-time end
                                        :to-id (:db/id drag-over-booking))
          to-index          (or (:ordered/order drag-over-booking)
                                (count all-days-bookings))
          other-bookings    (->> all-days-bookings
                                 (remove #(-> % :db/id (= booking-id))))
          before            (->> other-bookings
                                 (take-while #(< (:ordered/order %) to-index))
                                 (map :db/id))
          after             (->> other-bookings
                                 (drop-while #(< (:ordered/order %) to-index))
                                 (map :db/id))
          new-order         (concat before [booking-id] after)]
      {:dispatch [::process/signal execution-id [:process.signal/dispatch-booking payload] {:on-success [::dispatch-success booking-id]}]
       :db       (-> (p/deep-merge :into db
                                   {::drag-over-booking          nil
                                    ::dragged-booking-auth-group nil
                                    ::entities/entity-db         (->> (map-indexed (fn [i id]
                                                                                     [id {:ordered/order i}])
                                                                                   new-order)
                                                                      (into {}))}
                                   {::entities/entity-db {booking-id {:domain/state              :booking.state/dispatched
                                                                      :booking/assignee          {:db/id inspector-id}
                                                                      :booking/actual-start-time start
                                                                      :booking/actual-end-time   end}}})
                     (update ::pending #(conj (or % #{}) booking-id)))})))


(rf/reg-event-db ::dispatch-success
  (fn [db [_ booking-id]]
    (update db ::pending disj booking-id)))

(rf/reg-sub ::pending
  :-> ::pending)

(rf/reg-sub ::no-dispatch-pending?
  :<- [::pending]
  :-> empty?)

(rf/reg-sub ::dispatch-pending?
  :<- [::pending]
  (fn [all-pending [_ booking-id]]
    (contains? all-pending booking-id)))

;; Cancel dispatch for booking
(rf/reg-event-fx ::cancel-dispatch
  (fn [{:keys [db]} [_ booking-id execution-id]]
    ; Only dispatched bookings allowed:
    (when (not= :booking.state/to-dispatch (get-in db [::entities/entity-db booking-id :domain/state]))
      (let [payload {:booking-id booking-id}]
        {:dispatch-n [[::entities/update-entity booking-id {:domain/state              :booking.state/to-dispatch
                                                            :booking/assignee          nil
                                                            :booking/actual-start-time nil
                                                            :booking/actual-end-time   nil
                                                            :ordered/order             nil}]
                      [::process/signal execution-id [:process.signal/cancel-dispatch payload]]]
         :db         (merge db {::drag-over-booking          nil
                                ::dragged-booking-auth-group nil})}))))


(rf/reg-event-fx ::send-mail
  (fn [_ [_ recipients additional-recipients notes booking-id]]
    {:comm/command {:command    :booking/send-dispatch-notification
                    :method     :post
                    :data       {:recipients            recipients
                                 :additional-recipients (->> additional-recipients
                                                             (map :email)
                                                             set)
                                 :notes                 notes
                                 :booking               booking-id}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :dispatching/mail-sent}}}))


(rf/reg-event-fx ::toggle-request-editor
  (fn [{:keys [db]} _]
    {:db       (update db ::request-editor-open? not)
     :dispatch [:app.front.view.dispatch.dispatch-booking/init]}))


(rf/reg-sub ::request-editor-open?
  (fn [db _]
    (-> db ::request-editor-open?)))


(rf/reg-event-db ::changes-to-dispatch-notes?
  (fn [db [_ value?]]
    (assoc db ::changes-to-dispatch-notes? value?)))


(rf/reg-event-fx ::save-dispatch-notes-now
  (fn [{:keys [db]} [_ execution-id booking-id]]
    (if (::changes-to-dispatch-notes? db)
      {:dispatch-n        [[::changes-to-dispatch-notes? false]
                           [::save-dispatch-notes true execution-id booking-id (::dispatch-notes-draft db)]]
       :dispatch-debounce [{:id     ::save-dispatch-notes-debounce
                            :action :cancel}]}
      {:dispatch [::changes-to-dispatch-notes? false]})))


;; Save dispatch notes
(rf/reg-event-fx ::save-dispatch-notes
  (fn [_ [_ notify? execution-id booking-id notes]]
    (let [payload (p/assoc-if {:booking-id booking-id}
                              :dispatch-notes notes)]
      {:comm/command {:command    :process/apply-signal
                      :data       {:process/signal [:process.signal/update-booking payload]
                                   :entity-id      execution-id}
                      :on-success [::dispatch-notes-saved notify?]}})))


(rf/reg-event-fx ::dispatch-notes-saved
  interceptors
  (fn [{:keys [_ tr]} [_ notify? status body]]
    {:dispatch-n (cond-> [[::entities/apply-changes status body]]
                   notify? (conj [:notifications/add :toast :success :success (tr [:booking/changes-saved])]))}))


(rf/reg-sub ::map-visible?
  :<- [:session/user]
  (fn [user _]
    (-> user :user/config-flags :user.config-flag/dispatch-map-hidden? not)))


(defn on-date-fn [date]
  (fn [{:booking/keys [actual-start-time]}]
    (and actual-start-time
         (date-util/same-day? actual-start-time date))))

(defn booking-filter [selected-date mode]
  (case mode
    :requests booking-model/to-dispatch?
    :cancelled (every-pred booking-model/cancelled?
                           (on-date-fn selected-date))))

(defn filter-by-group [use-unified-view? selected-group-id booking]
  (or use-unified-view?
      (-> booking booking-model/get-workspace :domain/authority-group :db/id (= selected-group-id))))

(rf/reg-sub ::bookings-to-show
  :<- [::entities/entities {:entity/type :entity.type/booking}]
  :<- [::date]
  :<- [::mode]
  :<- [::selected-authority-group-id]
  :<- [::use-unified-view?]
  (fn [[all-bookings date mode selected-group-id unified-view?] _]
    (filter (every-pred booking-model/get-workspace ;; display only when workspace has been exported to front-end too
                        (booking-filter date mode)
                        (partial filter-by-group unified-view? selected-group-id))
            all-bookings)))

(rf/reg-sub ::bookings-for-dispatch
  :<- [::bookings-to-show]
  :<- [::group-tz]
  :<- [::date-util/yearless-date-fn]
  :<- [::date-util/format-fn (merge date-util/yearless-date-opts {:year "numeric"})]
  (fn [[bookings group-tz format-yearless-date format-with-year] _]
    (->> bookings
         ;; First sort by weakest criteria, assuming sorting and grouping is stable
         (sort-by :entity/created #(date-util/before? (js/Date. %1) (js/Date. %2)))
         ;; First group by local date
         (group-by #(date-util/date-in-tz (:booking/requested-start-time %) group-tz))
         ;; Sort by date
         (sort-by first #(date-util/before? (js/Date. %1) (js/Date. %2)))
         ;; Then within a date, group by time slot (= requested start and end times)
         (map (fn [[_ bookings]]
                ;; We know that all bookings have the same date here, but we want to reformat for display
                [(let [start-date (-> bookings
                                      first
                                      :booking/requested-start-time
                                      (date-util/in-tz group-tz))]
                   (if (or (date-util/before-current-year? start-date)
                           (date-util/after-current-year? start-date))
                     (format-with-year start-date)
                     (format-yearless-date start-date)))
                 (->> bookings
                      (group-by (juxt :booking/requested-start-time :booking/requested-end-time))
                      (sort-by (fn [[start-time _] _] start-time)))])))))

(rf/reg-sub ::item-count
  :<- [::entities/entities {:entity/type :entity.type/booking}]
  :<- [::date]
  :<- [::selected-authority-group-id]
  :<- [::use-unified-view?]
  (fn [[bookings date selected-group-id unified-view?] [_ mode]]
    (->> bookings
         (filter (every-pred (booking-filter date mode)
                             (partial filter-by-group unified-view? selected-group-id)))
         count)))

(rf/reg-sub ::visible-inspector-ids
  :<- [::entities/entities {:entity/type :entity.type/booking}]
  :<- [::date]
  :<- [::mode]
  :<- [::visible-inspector-ids-db]
  (fn [[bookings date mode inspector-ids] _]
    (case mode
      :requests inspector-ids
      :cancelled (->> bookings
                      (filter (booking-filter date mode))
                      (map :booking/assignee)
                      (map :db/id)
                      set)
      inspector-ids)))

(rf/reg-sub ::dispatched-bookings
  :<- [::entities/entities {:entity/type :entity.type/booking}]
  (fn [bookings _]
    (->> bookings
         (filter :booking/assignee))))

(rf/reg-sub ::user-bookings
  :<- [::dispatched-bookings]
  :<- [::mode]
  (fn [[bookings mode] [_ user-id]]
    (->> bookings
         (filter #(-> % :booking/assignee :db/id (= user-id)))
         (filter (case mode
                   :requests (complement booking-model/cancelled?)
                   :cancelled booking-model/cancelled?)))))

(rf/reg-sub ::user-bookings-for-date
  (fn [[_ user-id]]
    [(rf/subscribe [::user-bookings user-id])
     (rf/subscribe [::date])])
  (fn [[bookings selected-date] _]
    (->> bookings
         (filter (fn [{:booking/keys [actual-start-time actual-end-time]}]
                   (and actual-start-time actual-end-time
                        (date-util/on-or-after? actual-start-time selected-date)
                        (date-util/before? actual-end-time (date-util/add-days-to-date selected-date 1)))))
         (sort-by :ordered/order))))

(rf/reg-sub ::user-time-slots+bookings-for-date
  (fn [[_ user-id]]
    [(rf/subscribe [::user-bookings-for-date user-id])
     (rf/subscribe [::time-slots-for-selected-date])
     (rf/subscribe [::date])
     (rf/subscribe [::group-tz])])
  (fn [[user-bookings time-slots date group-tz] _]
    (let [slots+bookings (->> (sort-by (juxt :time-slot/start-hour :time-slot/start-minute) time-slots)
                              (map (fn [{:time-slot/keys [start-hour start-minute end-hour end-minute] :as slot}]
                                     [slot
                                      (let [slot-start-time (date-util/date-at-time-in-tz date start-hour start-minute group-tz)
                                            slot-end-time   (date-util/date-at-time-in-tz date end-hour end-minute group-tz)]
                                        (->> user-bookings
                                             (filter (fn [{:booking/keys [actual-start-time]}]
                                                       (and actual-start-time
                                                            (date-util/on-or-after? actual-start-time slot-start-time)
                                                            (date-util/before? actual-start-time slot-end-time))))))])))
          in-slot?       (->> slots+bookings
                              (mapcat second)
                              set)
          overflow-books (->> user-bookings
                              (remove in-slot?)
                              seq)]
      (cond-> slots+bookings
        overflow-books (concat [[nil overflow-books]])))))

(rf/reg-sub ::user-overflow-bookings-for-date
  (fn [[_ user-id]]
    (rf/subscribe [::user-time-slots+bookings-for-date user-id]))
  (fn [time-slots+bookings _]
    (->> time-slots+bookings
         (filter #(-> % first nil?))
         first
         last
         (sort-by :booking/actual-start-time)
         seq)))

;; This is used to provide bookings for the dispatch map
(rf/reg-sub ::bookings-for-date
  :<- [::entities/entities (filter #(and (booking-model/is-booking? %)
                                         (-> % :domain/state (not= :booking.state/cancelled))))]
  :<- [::date]
  :<- [::use-unified-view?]
  :<- [::selected-authority-group-id]
  :<- [::visible-inspectors]
  (fn [[bookings selected-date unified-view? selected-group-id visible-inspectors] _]
    (let [inspector-ids (set (keep :db/id visible-inspectors))]
      (->> bookings
           (filter (fn [{:booking/keys [requested-start-time requested-end-time actual-start-time actual-end-time] :as b}]
                     (let [[start end] (if (-> b :domain/state (= :booking.state/to-dispatch))
                                         [requested-start-time requested-end-time]
                                         [actual-start-time actual-end-time])]
                       (and start end
                            (date-util/on-or-after? start selected-date)
                            (date-util/before? end (date-util/add-days-to-date selected-date 1))
                            (or unified-view?
                                ;; already dispatched bookings will be shown
                                (and actual-start-time
                                     ;; but only if the inspector is visible at the moment
                                     (->> b :booking/assignee :db/id (contains? inspector-ids)))
                                (filter-by-group unified-view? selected-group-id b))))))
           (sort-by :ordered/order)))))


(rf/reg-event-db ::select-inspector-id
  (fn [db [_ inspector]]
    (-> (assoc db ::selected-inspector-id inspector)
        (dissoc ::selected-booking-id))))


(rf/reg-event-db ::deselect-inspector
  (fn [db _]
    (dissoc db ::selected-inspector-id)))


(rf/reg-sub ::selected-inspector-id :-> ::selected-inspector-id)


(rf/reg-sub ::selected-inspector
  :<- [::visible-inspectors]
  :<- [::selected-inspector-id]
  (fn [[inspectors selected-id] _]
    (when selected-id
      (p/find-first inspectors {:db/id selected-id}))))


(rf/reg-sub ::working-areas-feature-collection :-> ::working-areas-feature-collection)

(rf/reg-event-fx ::copy-booking
  (fn [_ [_ booking-id]]
    {:comm/command {:command    :booking/copy
                    :data       {:booking-id booking-id}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::set-fulfilled
  (fn [_ [_ booking-id]]
    {:comm/command {:command    :booking/set-fulfilled
                    :data       {:booking-id booking-id}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-sub ::available-inspector-sort-options
  :<- [::working-areas-feature-collection]
  (fn [{:keys [features]} _]
    (concat core-inspector-sort-options
            (when (seq features)
              [:dispatching.inspector-sort/location]))))

(rf/reg-event-fx ::set-inspector-sort-option
  (fn [{:keys [db]} [_ k enabled?]]
    (let [enabled-set (-> (enabled-inspector-sort-options db)
                          ((if enabled? conj disj) k))]
      {:local-storage-set! [inspector-local-storage-key (pr-str enabled-set)]
       :db                 (assoc db ::enabled-inspector-sort-options enabled-set)
       :dispatch           [::update-sort-order]})))

(rf/reg-sub ::enabled-inspector-sort-options
  :-> enabled-inspector-sort-options)

(rf/reg-sub ::assign-to-unqualified?
  :-> #(get % ::assign-to-unqualified? false))

(rf/reg-event-db ::set-assign-to-unqualified
  (fn [db [_ v]]
    (-> db (assoc ::assign-to-unqualified? v))))
